import {
  buildOnboardingHandoffAuthDraftContextKey,
  storeOnboardingAuthDraft,
  storeOnboardingHandoffToken,
  withAuthReturnPath,
} from "./onboarding-auth.js";

export const CHECKOUT_EMAIL_REQUIRED_MESSAGE = "Add and verify your email before paying.";

export function isCheckoutEmailRequiredError(error) {
  return error instanceof Error
    && String(error.message || "").trim() === CHECKOUT_EMAIL_REQUIRED_MESSAGE;
}

export function buildOnboardingCheckoutReturnPath({
  forceLive = false,
  accountId = "",
  mode = "new",
  selectedPlan = "",
  selectedCadence = "",
} = {}) {
  const params = new URLSearchParams({
    step: "pricing",
    mode: String(mode || "").trim().toLowerCase() === "existing" ? "existing" : "new",
  });
  if (forceLive) {
    params.set("forceLive", "1");
  }
  if (String(accountId || "").trim()) {
    params.set("accountId", String(accountId || "").trim());
  }
  if (String(selectedPlan || "").trim()) {
    params.set("selectedPlan", String(selectedPlan || "").trim());
  }
  if (String(selectedCadence || "").trim()) {
    params.set("selectedCadence", String(selectedCadence || "").trim());
  }
  return `/setup?${params.toString()}`;
}

export function buildLinkedEmailCaptureAuthPath({ handoffToken = "", returnPath = "" } = {}) {
  const normalizedToken = String(handoffToken || "").trim();
  const authPath = normalizedToken
    ? `/auth?context=linked&handoff=${encodeURIComponent(normalizedToken)}`
    : "/auth?context=linked";
  return withAuthReturnPath(authPath, returnPath);
}

export function storeBillingEmailCaptureDraft(handoffToken = "") {
  const normalizedToken = String(handoffToken || "").trim();
  if (!normalizedToken) {
    return;
  }
  storeOnboardingHandoffToken(normalizedToken);
  storeOnboardingAuthDraft(
    {
      status: "idle",
      userMode: "new",
      languageStepComplete: true,
    },
    buildOnboardingHandoffAuthDraftContextKey(normalizedToken),
  );
}

export async function routeOnboardingCheckoutEmailGate({
  accountId = "",
  createHandoff,
  forceLive = false,
  mode = "new",
  navigate,
  selectedCadence = "",
  selectedPlan = "",
} = {}) {
  if (typeof createHandoff !== "function") {
    throw new Error("We could not open email verification for checkout.");
  }
  const handoff = await createHandoff(accountId);
  const handoffToken = String(handoff?.token || "").trim();
  if (!handoffToken) {
    throw new Error("We could not open email verification for checkout.");
  }
  storeBillingEmailCaptureDraft(handoffToken);
  const nextPath = buildLinkedEmailCaptureAuthPath({
    handoffToken,
    returnPath: buildOnboardingCheckoutReturnPath({
      accountId,
      forceLive,
      mode,
      selectedCadence,
      selectedPlan,
    }),
  });
  if (typeof navigate === "function") {
    navigate(nextPath);
  }
  return nextPath;
}

export async function routeOnboardingCheckoutEmailGateIfNeeded(error, options = {}) {
  if (!isCheckoutEmailRequiredError(error)) {
    return false;
  }
  try {
    await routeOnboardingCheckoutEmailGate(options);
  } catch (handoffError) {
    if (typeof options.onError === "function") {
      options.onError(handoffError instanceof Error ? handoffError.message : "Could not open email verification for checkout.");
    }
  }
  return true;
}
